import { forwardRef } from "react"
import type { TransitionProps } from "@mui/material/transitions"
import type { RootState } from "@/store"
import { Alert, Box, Button, CircularProgress, Dialog, Paper, Slide, Stack, Typography, useMediaQuery } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { formatarBRL } from "@/lib/formato"
import { avisoDeMudancaDePreco } from "@/lib/reconciliar-carrinho"
import { alterarQuantidadeItem, atualizarKiosk, definirObservacaoItemKiosk, duplicarItemKiosk, removerItemKiosk, type CartItem } from "@/store/self-service-kiosk-slice"
import { complementosAchatados } from "./carrinho-totem"
import ScreenHeader from "../components/screen-header"
import { ListaItensPedido, type ItemPedidoListado } from "@/components/lista-itens-pedido"
import { EstadoVazio } from "@/components/estado-mensagem"
import { ESTILO_ACAO_KIOSK } from "@/components/catalogo-kiosk/tema-kiosk"
import { useImagensPorProduto } from "@/lib/imagens-catalogo"
import { convitePorQuantidade } from "@/lib/itens-pedido"
import AddIcon from '@mui/icons-material/Add'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'

const Transition = forwardRef(function Transition(props: TransitionProps & { children: React.ReactElement }, ref: React.Ref<unknown>,) {
    return <Slide direction="up" ref={ref} {...props} />
})

const SX_ACAO = { ...ESTILO_ACAO_KIOSK, '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 28 } } as const

interface AddItemsReviewProps {
    onConfirm: () => void
    onEditItem: (item: CartItem) => void
    enviando?: boolean
}

function AddItemsReview({ onConfirm, onEditItem, enviando = false }: AddItemsReviewProps): JSX.Element {
    const dispatch = useDispatch()
    const cartItems = useSelector((state: RootState) => state.selfServiceKiosk.cartItems)
    const showReviewItems = useSelector((state: RootState) => state.selfServiceKiosk.showReviewItems)
    const alteracoesPreco = useSelector((state: RootState) => state.selfServiceKiosk.alteracoesPreco)
    const imagemPorProduto = useImagensPorProduto('CARDAPIO_TOTEM_CHECKOUT')
    const aviso = avisoDeMudancaDePreco(alteracoesPreco, formatarBRL)
    const deitado = useMediaQuery('(orientation: landscape) and (min-width: 820px)')

    const totalItens = cartItems.reduce((s, i) => s + i.quantidade, 0)
    const totalPreco = cartItems.reduce((s, i) => s + i.valorTotal, 0)
    const vazio = cartItems.length === 0

    const fechar = (): void => {
        dispatch(atualizarKiosk({ showReviewItems: false }))
    }

    const editar = (id: string): void => {
        const item = cartItems.find((c) => c.id === id)
        if (item) onEditItem(item)
    }

    const itensListados: ItemPedidoListado[] = cartItems.map((item) => ({
        id: item.id,
        nome: item.nome,
        quantidade: item.quantidade,
        valorTotal: item.valorTotal,
        imagemUrl: imagemPorProduto.get(item.idProduto),
        observacao: item.observacao,
        editavel: item.temPassos,
        subitens: complementosAchatados(item).map((sub) => ({
            nome: sub.nome,
            quantidade: sub.quantidade,
            valorTotal: sub.valor * sub.quantidade,
        })),
        avisoFaixa: convitePorQuantidade({
            faixasPreco: item.faixasPreco,
            quantidade: item.quantidade,
            precoUnitario: item.valorUnitario,
        }),
    }))

    return (
        <Dialog
            open={showReviewItems}
            fullScreen
            onClose={enviando ? undefined : fechar}
            TransitionComponent={Transition}
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: 'background.default',
                        userSelect: 'none',
                        WebkitTapHighlightColor: 'transparent',
                        '& input, & textarea': { userSelect: 'text' },
                    },
                },
            }}
        >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <ScreenHeader
                    title="Conferir itens"
                    subTitle={`${totalItens} ${totalItens === 1 ? 'item a adicionar' : 'itens a adicionar'}`}
                    onClickBack={fechar}
                    voltarDesabilitado={enviando}
                />
            </Box>

            <Stack sx={{ height: '100%', overflow: 'hidden' }}>
                {vazio ? (
                    <EstadoVazio
                        tamanho="grande"
                        icone={<ShoppingCartIcon />}
                        titulo="Nenhum item adicionado ainda"
                        descricao="Volte ao cardápio para escolher o que vai entrar na conta."
                        acao={
                            <Button variant="contained" size="large" onClick={fechar} sx={{ py: 1.5, px: 4, fontSize: '1.1rem', textTransform: 'none' }}>
                                Voltar ao cardápio
                            </Button>
                        }
                    />
                ) : (
                    <Stack direction={deitado ? 'row' : 'column'} sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Stack
                            sx={{
                                flexGrow: 1,
                                px: 2,
                                overflowY: 'auto',
                                overscrollBehavior: 'contain',
                                scrollbarWidth: 'thin',
                                scrollbarColor: 'rgba(120, 130, 140, .45) transparent',
                                '&::-webkit-scrollbar': { width: 8 },
                                '&::-webkit-scrollbar-thumb': { borderRadius: 8, backgroundColor: 'rgba(120, 130, 140, .45)' },
                            }}
                        >
                            {aviso && (
                                <Alert
                                    severity={aviso.tom}
                                    onClose={() => dispatch(atualizarKiosk({ alteracoesPreco: [] }))}
                                    sx={{ mt: 2, fontSize: '1.05rem' }}
                                >
                                    {aviso.texto}
                                </Alert>
                            )}

                            <Box sx={{ mt: -1.5 }}>
                                <ListaItensPedido
                                    tamanho="grande"
                                    itens={itensListados}
                                    tecladoProprio
                                    acoes={{
                                        desabilitado: enviando,
                                        minimoUm: true,
                                        aoAlterarQuantidade: (id, delta) => dispatch(alterarQuantidadeItem({ id, delta })),
                                        aoEditar: editar,
                                        aoDuplicar: (id) => dispatch(duplicarItemKiosk(id)),
                                        aoRemover: (id) => dispatch(removerItemKiosk(id)),
                                        aoAlterarObservacao: (id, observacao) => dispatch(definirObservacaoItemKiosk({ id, observacao })),
                                    }}
                                />
                            </Box>
                        </Stack>

                        <Paper
                            elevation={0}
                            square
                            sx={{
                                flexShrink: 0,
                                width: deitado ? 'clamp(320px, 34%, 460px)' : '100%',
                                maxHeight: deitado ? undefined : '45%',
                                overflowY: deitado ? undefined : 'auto',
                                borderLeft: deitado ? 1 : 0,
                                borderTop: deitado ? 0 : 1,
                                borderColor: 'divider',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }} />

                            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                                    Total
                                </Typography>

                                <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                                    + {formatarBRL(totalPreco)}
                                </Typography>
                            </Stack>

                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={enviando}
                                onClick={onConfirm}
                                startIcon={enviando ? <CircularProgress size={28} color="inherit" /> : <AddShoppingCartIcon />}
                                sx={SX_ACAO}
                            >
                                {enviando ? 'Adicionando...' : 'Adicionar ao pedido'}
                            </Button>

                            <Button
                                size="large"
                                variant="outlined"
                                disabled={enviando}
                                startIcon={<AddIcon />}
                                onClick={fechar}
                                sx={SX_ACAO}
                            >
                                Adicionar mais itens
                            </Button>
                        </Paper>
                    </Stack>
                )}
            </Stack>
        </Dialog>
    )
}

export default AddItemsReview
